/**
 * Image-area global state.
 *
 * The image-area tool draws a polygon, fetches Feltkatalog images inside it
 * (`FileManager/List` with an `area` filter) and shows them as clustered
 * billboards. This module owns the canonical state for that mode: the polygon,
 * the fetched images, and what the sidebar panel is currently showing.
 *
 * Mirrors `area-mode-state.svelte.ts` — a `.svelte.ts` module so components
 * get fine-grained reactivity by reading properties directly.
 */

import type { Entity } from 'cesium';
import type { ISmartData } from '$lib/models/ISmartData';
import type { IProjectModel } from '$lib/models/IProjectModel';

/**
 * Max images billboarded on the map (one page of this size is fetched).
 * The API rejects pageSize > 200 with a 400, so this is the hard ceiling.
 */
export const IMAGE_AREA_BILLBOARD_CAP = 200;

class ImageAreaState {
	/** True once a polygon is drawn and images are shown/loading. */
	active = $state(false);

	/** GeoJSON polygon (closed ring) — the `area` filter sent to the API. */
	polygon = $state<IProjectModel.Area | null>(null);

	/** The Cesium polygon entity drawn on the map. */
	polygonEntity = $state<Entity | null>(null);

	/** Whether the polygon is the selected entity (drives its highlight). */
	polygonSelected = $state(false);

	/** Billboarded images (first page, capped at {@link IMAGE_AREA_BILLBOARD_CAP}). */
	images = $state<ISmartData.ImageItem[]>([]);

	/** Total hits inside the polygon (may exceed the billboard cap). */
	totalCount = $state(0);

	loading = $state(false);
	error = $state<string | null>(null);

	/**
	 * What the sidebar lists:
	 *  - `'cluster'` — exactly the images from a clicked cluster/billboard.
	 *  - `'all'` — every image in the polygon (paginated, grouped by month).
	 */
	sidebarMode = $state<'cluster' | 'all'>('all');

	/** Image ids from the clicked cluster (or the single clicked billboard). */
	clickedImageIds = $state<number[]>([]);

	/**
	 * Image id whose lightbox should open automatically — set when a single
	 * billboard is clicked, consumed (nulled) by the panel once opened.
	 */
	autoOpenImageId = $state<number | null>(null);

	// ── Derived ────────────────────────────────────────────────────────────

	/** The clicked cluster's images, resolved against the billboarded set. */
	readonly clickedImages = $derived(
		this.images.filter((img) => this.clickedImageIds.includes(img.id))
	);

	// ── Mutators ───────────────────────────────────────────────────────────

	/** Sidebar → the clicked-cluster listing. */
	showCluster(imageIds: number[], autoOpenId: number | null = null): void {
		this.sidebarMode = 'cluster';
		this.clickedImageIds = imageIds;
		this.autoOpenImageId = autoOpenId;
	}

	/** Sidebar → the full paginated listing. */
	showAll(): void {
		this.sidebarMode = 'all';
		this.clickedImageIds = [];
		this.autoOpenImageId = null;
	}

	/** Resets every field to its default — called when the area is removed. */
	reset(): void {
		this.active = false;
		this.polygon = null;
		this.polygonEntity = null;
		this.polygonSelected = false;
		this.images = [];
		this.totalCount = 0;
		this.loading = false;
		this.error = null;
		this.sidebarMode = 'all';
		this.clickedImageIds = [];
		this.autoOpenImageId = null;
	}
}

export const imageAreaState = new ImageAreaState();
